import { Component, DestroyRef, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PaginationComponent } from '../inputs/pagination/pagination.component';
import { SearchInputComponent } from '../inputs/search-input/search-input.component';
import { SortDropdownComponent, SortDropdownGroup } from '../inputs/sort-dropdown/sort-dropdown.component';
import { MessageTemplate } from '../../services/message-templates/message.template';
import { BroadcastMessageService } from '../../services/broadcast/broadcast.service';
import { PromptCardComponent } from '../toasts-and-cards/prompt-card/prompt-card.component';
import { HasPermissionDirective } from '../../directives/has-permission.directive';
import { EmptyStateComponent } from '../ui/empty-state/empty-state.component';
import { DataTableComponent } from '../ui/data-table/data-table.component';
import { SmsTextComponent } from '../messages/sms-text/sms-text.component';
import { formatWhen, smsParts } from '../../../utils/sms';

type TemplateSort = '' | 'nameAsc' | 'nameDesc' | 'createdDesc' | 'createdAsc' | 'modifiedDesc' | 'modifiedAsc';

/** One template row, worked out once so the template only reads fields. */
interface TemplateRow {
  template: any;
  key: string | number;
  name: string;
  copy: string;
  category: string;
  parts: string;
  partsHint: string | null;
  created: string;
}

@Component({
  selector: 'message-template-table',
  standalone: true,
  imports: [
    PaginationComponent,
    SearchInputComponent,
    SortDropdownComponent,
    PromptCardComponent,
    HasPermissionDirective,
    EmptyStateComponent,
    DataTableComponent,
    SmsTextComponent,
    MatTooltipModule,
  ],
  templateUrl: './message-table.component.html',
  styleUrls: ['./message-table.component.scss'],
})
export class MessageTemplateTable implements OnChanges {
  private router = inject(Router);
  private messageTemplate = inject(MessageTemplate);
  private broadcastService = inject(BroadcastMessageService);
  private destroyRef = inject(DestroyRef);

  @Input() template_data: any[] = []; // Pass data from parent
  @Input() loading: boolean = false;
  @Input() meta: any = {};
  @Input() startItem: number = 0;
  @Input() endItem: number = 0;

  showDeletePrompt: boolean = false;
  selectedTemplate: { name: string; id: string } = { name: '', id: '' };
  pageSize: number = 10;

  // ── Search and sort ──
  searchValue: string = '';
  sortValue: TemplateSort = '';

  readonly sortGroups: SortDropdownGroup[] = [
    {
      label: 'Name',
      options: [
        { value: 'nameAsc', title: 'A → Z', icon: 'sort_by_alpha' },
        { value: 'nameDesc', title: 'Z → A', icon: 'sort_by_alpha' },
      ],
    },
    {
      label: 'Date created, this page',
      options: [
        { value: 'createdDesc', title: 'Newest first', icon: 'schedule' },
        { value: 'createdAsc', title: 'Oldest first', icon: 'history' },
      ],
    },
    {
      label: 'Date modified, this page',
      options: [
        { value: 'modifiedDesc', title: 'Newest first', icon: 'edit_note' },
        { value: 'modifiedAsc', title: 'Oldest first', icon: 'draw' },
      ],
    },
  ];

  /** Results fetched here when searching or sorting by name across every page. */
  private serverData: any[] = [];
  serverMeta: { currentPage: number; totalPages: number; totalElements: number } = {
    currentPage: 0,
    totalPages: 1,
    totalElements: 0,
  };
  serverLoading = false;
  serverError = '';
  private searchRequest?: Subscription;

  rows: TemplateRow[] = [];

  constructor() {
    this.destroyRef.onDestroy(() => this.searchRequest?.unsubscribe());
  }

  /** Searching, or sorting by name, asks the server for every page. */
  get serverMode(): boolean {
    return !!this.searchValue.trim() || this.sortValue === 'nameAsc' || this.sortValue === 'nameDesc';
  }

  get isLoading(): boolean {
    return this.serverMode ? this.serverLoading : this.loading;
  }

  get currentPage(): number {
    return this.serverMode ? this.serverMeta.currentPage : this.meta?.currentPage ?? 0;
  }

  get totalPages(): number {
    return this.serverMode ? this.serverMeta.totalPages : this.meta?.totalPages ?? 1;
  }

  get totalItems(): number {
    return this.serverMode ? this.serverMeta.totalElements : this.meta?.totalElements ?? 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['template_data'] && !this.serverMode) {
      this.buildRows();
    }
  }

  private buildRows() {
    const source = this.serverMode ? this.serverData : this.template_data || [];
    const sorted = this.sortRows(Array.isArray(source) ? [...source] : []);
    this.rows = sorted.map((template: any, index: number): TemplateRow => {
      const copy = String(template?.content ?? '');
      const count = copy ? smsParts(copy) : null;
      return {
        template,
        key: template?.id ?? template?.uuid ?? `row-${index}`,
        name: String(template?.name ?? '').trim() || 'Untitled template',
        copy,
        category: String(template?.category ?? '').trim() || '—',
        // "About 2 parts" in the cell itself, as compose's template browser shows it.
        parts: !count?.parts
          ? '—'
          : `${count.estimated ? 'About ' : ''}${count.parts === 1 ? '1 part' : `${count.parts} parts`}`,
        partsHint: count?.estimated ? 'Estimate: personalised fields change the length for each contact' : null,
        created: formatWhen(template?.createdAt),
      };
    });
  }

  /** Date sorts reorder the page on screen; name sorts come back sorted from the server. */
  private sortRows(data: any[]): any[] {
    const time = (value: unknown) => {
      const date = new Date(String(value ?? ''));
      return Number.isNaN(date.getTime()) ? 0 : date.getTime();
    };
    switch (this.sortValue) {
      case 'createdDesc':
        return data.sort((a, b) => time(b.createdAt) - time(a.createdAt));
      case 'createdAsc':
        return data.sort((a, b) => time(a.createdAt) - time(b.createdAt));
      case 'modifiedDesc':
        return data.sort((a, b) => time(b.updatedAt) - time(a.updatedAt));
      case 'modifiedAsc':
        return data.sort((a, b) => time(a.updatedAt) - time(b.updatedAt));
      default:
        return data;
    }
  }

  handleValueChange(newValue: string) {
    this.searchValue = newValue ?? '';
    this.refresh(0);
  }

  handleSortChange(option: string) {
    this.sortValue = option as TemplateSort;
    this.refresh(0);
  }

  /** Re-read whichever source is showing. */
  private refresh(page: number) {
    if (this.serverMode) {
      this.runSearch(page);
    } else {
      this.searchRequest?.unsubscribe();
      this.serverLoading = false;
      this.serverError = '';
      this.buildRows();
    }
  }

  runSearch(page: number = 0) {
    this.searchRequest?.unsubscribe();
    this.serverLoading = true;
    this.serverError = '';
    const sortBy = this.sortValue === 'nameAsc' || this.sortValue === 'nameDesc' ? this.sortValue : undefined;
    this.searchRequest = this.broadcastService
      .searchTemplates({ page, size: this.pageSize, template_name: this.searchValue, sortBy })
      .subscribe({
        next: (result) => {
          this.serverData = result.content;
          this.serverMeta = {
            currentPage: result.currentPage,
            totalPages: result.totalPages,
            totalElements: result.totalElements,
          };
          this.serverLoading = false;
          this.buildRows();
        },
        error: () => {
          this.serverData = [];
          this.serverLoading = false;
          this.serverError = 'Templates did not load. Check your connection and try again.';
          this.buildRows();
        },
      });
  }

  // ── Pagination ──
  handleNext() {
    const next = this.currentPage + 1;
    if (this.serverMode) this.runSearch(next);
    else this.messageTemplate.fetchTemplates(next, this.pageSize);
  }

  handlePrev() {
    const prev = Math.max(0, this.currentPage - 1);
    if (this.serverMode) this.runSearch(prev);
    else this.messageTemplate.fetchTemplates(prev, this.pageSize);
  }

  handleChangePage(page: number) {
    if (this.serverMode) this.runSearch(page);
    else this.messageTemplate.fetchTemplates(page, this.pageSize);
  }

  handlePageSizeChange(size: number) {
    this.pageSize = size;
    if (this.serverMode) this.runSearch(0);
    else this.messageTemplate.fetchTemplates(0, this.pageSize);
  }

  // ── Row actions ──
  closeDeletePrompt() {
    this.showDeletePrompt = false;
  }

  openPrompt(row: any) {
    this.selectedTemplate = row;
    this.showDeletePrompt = true;
  }

  deleteContact() {
    this.messageTemplate.deleteTemplate(this.selectedTemplate?.id).subscribe(() => {
      this.closeDeletePrompt();
      if (this.serverMode) this.runSearch(this.currentPage);
    });
  }

  /** Start a new message from this template. */
  openEditContact(row: any) {
    const params = { template: JSON.stringify(row) };
    this.router.navigate(['/organization/messages/create'], {
      queryParams: params,
    });
  }

  viewTemplate(template: any) {
    this.router.navigate(['/organization/messages/templates/new'], {
      queryParams: { id: template.id },
    });
  }
}
